<template>
    <div :class="classCardForm">
        <div class="sticky top-0 z-10">
            <dx-toolbar>
                <dx-item
                    location="before"
                    widget="dxButton"
                    :options="{
                        icon: 'print',
                        hint: 'Распечатать',
                        onClick: onPrintDocumentsButtonClick,
                    }"
                />
            </dx-toolbar>
        </div>
        <div>
            <pca-print-result ref="printResultNewRef"> </pca-print-result>
        </div>
        <dx-scroll-view class="flex-grow overflow-auto">
            <pca-card-group
                v-for="(item, index) in meta.groups"
                :key="index"
                :label="item.label"
                :icon="item.icon"
                :collapsed="item.collapsed"
                :entity-selection="item.entitySelection"
                :components="getComponents(item)"
                :components-path="componentsPath"
                :callbacks="{ userDataChanged, notifyErrorMessage }"
                :server-data="serverData"
            />
        </dx-scroll-view>
    </div>
</template>

<script>
export default {
    name: 'UniversalForm',
};
</script>

<script setup>
import { computed, defineProps, onBeforeMount, provide, ref } from 'vue';
import { useStore } from 'vuex';
import { onBeforeRouteUpdate } from 'vue-router';

import { DxScrollView } from 'devextreme-vue';
import DxToolbar, { DxItem } from 'devextreme-vue/toolbar';
import { custom } from 'devextreme/ui/dialog';
import UiNotify from 'devextreme/ui/notify';

import { api } from '@/service/http';
import { deleteUserDataChanges, getUserDataChanges, saveUserDataChanges } from '@/service/userdata-service';
import AiCustomConfirmDialog from '@/utils/ai-custom-confirm-dialog';
import PcaCardGroup from '@/views/contracts/print-contract/components/pca-card-group.vue';
import pcaPrintResult from '@/views/contracts/print-contract/components/pca-print-result.vue';

const printResultNewRef = ref();

const props = defineProps({
    // метаданные для карточки
    meta: {
        type: Object,
        default: () => {},
    },
    // путь до всех компонентов оберток, надо указать от view и до последней директории
    componentsPath: {
        type: String,
        default: 'components',
    },
    // расширение тулбара кастомными элементами
    toolbarItems: {
        type: Array,
        default: () => [],
    },
    // вкл/выкл стандартных кнопок (пока две: обновить и сохранить)
    useDefaultButtons: {
        type: Boolean,
        default: true,
    },
    // использовать когда стандартное поведение надо переопределить
    defaultHandlers: {
        type: Object,
        default: () => ({ refresh: null, commit: null }),
    },
    // использовать для подключения бэка к стандартному поведению
    callbacks: {
        type: Object,
        default: () => ({
            refreshData: null,
            saveData: null,
            isDirty: null,
            sendUserData: null,
            getUserData: null,
        }),
    },
});

const serverData = ref({});
const userData = ref({});
const store = useStore();
const validationResult = ref([]);
const errMessageText = ref('');

const classCardForm = computed({
    get: () => {
        if (props.meta.inner) {
            return 'card-inner';
        } else {
            return 'ai-card flex flex-col flex-grow h-full overflow-hidden';
        }
    },
});

provide('serverData', serverData);
provide('userData', userData);
provide('validationResult', validationResult);

const saveButtonOptions = ref({
    icon: 'save',
    hint: 'Сохранить',
    onClick: () => onSaveDataButtonClick(),
    disabled: true,
});

const onErrorPopupShow = async msg => {
    const myDialog = custom({
        title: `Ошибка сохранения карточки`,
        messageHtml: msg,
        width: 500,
    });

    msg && (await myDialog.show());
};
const onSaveDataButtonClick = async () => {
    const response = await props.callbacks.saveData({ ...userData.value });
    if (response.success) {
        errMessageText.value = '';
        validationResult.value = [];
        await clearUserData();
        await refreshData();
        return;
    }
    errMessageText.value = response.errors.message;
    onErrorPopupShow(errMessageText.value);
    response.errors.items && validateForm(response.errors.items);
};

const validateForm = errors => {
    errors.forEach(el => {
        errMessageText.value += ` ${el.message}</br>`;
    });
    validationResult.value = errors;
};

const notifyErrorMessage = message => {
    UiNotify({
        message: message,
        type: 'error',
        closeOnClick: true, // This option allows the notification to be closed when clicked
    });
};

onBeforeMount(async () => {
    await refreshData();
});

async function userDataChanged() {
    await sendUserData();
    saveButtonOptions.value = { ...saveButtonOptions.value, disabled: !isDirtyController() };
}

function isDirtyController() {
    if (props.callbacks.isDirty === null) {
        return isDirtyInternal(props.meta, serverData.value, userData.value);
    } else {
        return props.callbacks.isDirty(props.meta, serverData.value, userData.value);
    }
}

const isDirtyInternal = (meta, serverData, userData) => {
    // Перечисление ключей объекта serverData
    const serverDataKeys = Object.keys(serverData);
    for (const key of serverDataKeys) {
        if (JSON.stringify(serverData[key]) !== JSON.stringify(userData[key])) {
            return true;
        }
    }
    return false;
};

const refreshData = async () => {
    const refreshDataResult =
        props.callbacks.refreshData &&
        (await props.callbacks.refreshData(store.state.userDataDetails.focusedInstanceId));

    const userDataDB = await getUserData();

    if (userDataDB.length > 0) {
        userData.value = userDataDB[0];
    } else {
        if (refreshDataResult && refreshDataResult.data) {
            userData.value = JSON.parse(JSON.stringify(refreshDataResult.data));
        }
    }

    if (refreshDataResult && refreshDataResult.result) {
        serverData.value = refreshDataResult.data;
        await store.dispatch('setUserDataRawData', serverData.value);
    }
    saveButtonOptions.value = { ...saveButtonOptions.value, disabled: !isDirtyController() };
};

const getUserData = async () => {
    const args = {
        viewId: store.state.userDataDetails.focusedViewId,
        instanceId: store.state.userDataDetails.focusedInstanceId,
    };
    if (props.callbacks.getUserData) {
        return await props.callbacks.getUserData(args);
    }
    return await getUserDataChanges(args);
};

const sendUserData = async () => {
    const args = {
        viewId: store.state.userDataDetails.focusedViewId,
        instanceId: store.state.userDataDetails.focusedInstanceId,
        changes: [userData.value],
    };
    if (props.callbacks.sendUserData) {
        return await props.callbacks.sendUserData(args);
    }

    return await saveUserDataChanges(args);
};

const clearUserData = async () => {
    const args = {
        viewId: store.state.userDataDetails.focusedViewId,
        instanceId: store.state.userDataDetails.focusedInstanceId,
        changes: [],
    };

    return await deleteUserDataChanges(args);
};

const onRefreshDataButtonClick = async () => {
    if (isDirtyController()) {
        const result = await AiCustomConfirmDialog('У вас есть несохраненные изменения, сохранить изменения?');
        if (result === 1) {
            await onSaveDataButtonClick();
            await clearUserData();
            await refreshData();
        }
        if (result === 0) {
            errMessageText.value = '';
            validationResult.value = [];
            await clearUserData();
            await refreshData();
        }
    } else {
        errMessageText.value = '';
        validationResult.value = [];
        await clearUserData();
        await refreshData();
    }
};

const onPrintDocumentsButtonClick = async () => {
    const response = await getPrintResult();

    alert(`response = ${JSON.stringify(response)}`);

    const printResultArr = [];
    for (let ixRow = 0; ixRow < response.rows.length; ixRow++) {
        for (let ixFiles = 0; ixFiles < response.rows[ixRow].files.length; ixFiles++) {
            printResultArr.push({
                contractID: response.rows[ixRow].contractID,
                templID: response.rows[ixRow].files[ixFiles].templID,
                docID: response.rows[ixRow].files[ixFiles].docID,
                fn: response.rows[ixRow].files[ixFiles].fn,
                stor: response.rows[ixRow].files[ixFiles].stor,
            });
        }
    }

    printResultNewRef.value.refreshData(printResultArr);
    printResultNewRef.value.viewPopup();
};

const getPrintResult = async () => {
    // дата печати (по состоянию на) хранится в виде строки вида 2025-01-21T15:44:06.325Z, поэтому пробуем распарсить её в миллисекунды и создать объект типа Date для удобства дальнейшей обработки
    const printDate = new Date(Date.parse(userData.value['as_of_date']));

    const numberMonth = printDate.getMonth() + 1;
    // преобразуем месяц в строку и добавляем '0' к номерам месяцев, состоящих из одного символа
    const strMonth = numberMonth > 9 ? String(numberMonth) : '0' + String(numberMonth);
    // формируем строку с датой печати (по состоянию на) в виде, который требуется на back-е
    const strPrintDate = String(printDate.getDate()) + '.' + strMonth + '.' + String(printDate.getFullYear());
    // формируем строку с годом печати
    const strYear = String(printDate.getFullYear());

    const codeAnnexForm = userData.value['annex_form_entity_selection_id'];

    let annexForm;

    switch (codeAnnexForm) {
        case 1: {
            annexForm = 'FL';
            break;
        }
        case 2: {
            annexForm = 'Sh';
            break;
        }
        default: {
            annexForm = '';
        }
    }

    const contractIDs = userData.value['list_contract_value'];

    const objectIDs = userData.value['list_object_value'];

    const annexIDs = userData.value['list_annex_value'];

    const printContract = userData.value['must_be_printed_contract'];

    const printAnnexes = userData.value['must_be_printed_annexes'];

    const printStandardLetters = userData.value['must_be_printed_standard_letters'];

    const printClaims = userData.value['must_be_printed_claims'];

    const request = {
        printContract: printContract,
        contractIDs: contractIDs,
        annexIDs: annexIDs,
        annexForm: annexForm,
        objectIDs: objectIDs,
        printDate: strPrintDate,
        year: strYear,
    };

    return await api('counteragent./report/template/package', request);
};

const getComponents = group => {
    return props.meta?.components.filter(item => item.groupId === group.id);
};

onBeforeRouteUpdate(async () => {
    await refreshData();
});
defineExpose({
    onRefreshDataButtonClick,
});
</script>

<style lang="scss" scoped>
.card-inner {
    padding-right: 15px;
}

:deep(.dx-field) {
    display: flex;
    flex-direction: column;
}

:deep(.dx-texteditor.dx-editor-filled) {
}

:deep(.dx-field-label) {
    display: flex;
    flex-grow: 1;
    align-self: start;
    font-weight: 400;
    font-size: 12px;
    min-height: 1rem;
    width: 100%;
    padding-bottom: 3px;
}

:deep(.dx-field-value) {
    width: 100% !important;
}

:deep(.dx-field-left) {
    flex-direction: row-reverse;
    justify-content: flex-start;

    padding-top: 20px;
}

:deep(.dx-field-label-left) {
    display: flex;
    justify-content: flex-start;
    align-self: center;
}

:deep(.dx-field-value-left) {
    max-width: 28px;
    display: flex;
    justify-content: flex-start;
}

:deep(.dx-texteditor.dx-editor-filled) {
    border-radius: 5px;
    min-height: 32px;
}

:deep(.dx-texteditor-container) {
    border-radius: 5px;
    min-height: 32px;
}

:deep(.dx-texteditor-input) {
    font-size: 12px;
}

:deep(.ai-field-modified) {
    background-color: rgba(139, 195, 74, 0.32);

    .dx-texteditor-container {
        background-color: transparent !important;
    }
}

:deep(.ai-field-required) {
    background-color: #ffc0cbb5;
}

:deep(.ai-dx-invalid) {
    cursor: pointer;
    position: absolute;
    top: 65%;
    right: 15px;
    margin-top: -11px;
    width: 22px;
    height: 22px;
    text-align: center;
    line-height: 23px;
    font-size: 14px;
    font-weight: 500;
    background-color: #f44336;
    color: #fff;
    content: '!';
    border-radius: 50%;
}

:deep(.ai-dx-invalid::after) {
    cursor: pointer;
    position: absolute;
    top: 50%;
    right: 40%;
    margin-top: -11px;
    text-align: center;
    line-height: 23px;
    font-size: 14px;
    font-weight: 500;
    background-color: #ffffff00;
    color: #fff;
    content: '!';
    border-radius: 50%;
}

:deep(.dx-button.dx-button-has-icon:not(.dx-button-has-text):not(.dx-shape-standard)) {
    background-color: #f5f5f5;
}

:deep(.dx-button.dx-button-has-icon.dx-state-hover:not(.dx-button-has-text):not(.dx-shape-standard)) {
    background-color: #fff;
}

:deep(.dx-toolbar .dx-toolbar-after) {
    padding-inline-end: 15px;
}

.ai-card {
    display: flex;
    padding: 0 0 40px 30px;
    background-color: #ffffff;

    .ai-card-toolbar-container {
        margin-left: auto;
        padding: 0 30px 6px 0;
        max-width: 150px;
    }

    .ai-card-toolbar-container > .dx-toolbar {
        background-color: var(--light-blue);
        border-radius: 5px;
    }

    .ai-card-toolbar-container > .dx-toolbar > .dx-toolbar-items-container {
        height: 70px;
    }

    .ai-card-scroll-panel:deep(.dx-scrollable-wrapper) > .dx-scrollable-container {
        padding-right: 30px;
    }

    .ai-card-scroll-panel {
    }
}
</style>
